import {
  commandResultSchema,
  currentSessionSchema,
  demoPersonaSchema,
  demoSessionSelectionSchema,
  errorResponseSchema,
  platformAdministrationSchema,
  prototypeBootstrapSchema,
  sessionMutationResultSchema,
  type PrototypeCommand,
} from "@peer-start/platform-contracts";
import type { z } from "zod";

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly payload?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(path: `/${string}`, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const response = await fetch(`${import.meta.env.BASE_URL.replace(/\/$/, "")}${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body === undefined ? {} : { "content-type": "application/json" }),
      ...init?.headers,
    },
  });
  const payload: unknown = await response.json();
  if (!response.ok) {
    if (
      response.status === 401 &&
      typeof payload === "object" &&
      payload !== null &&
      "code" in payload &&
      payload.code === "login_required"
    ) {
      window.dispatchEvent(new Event("peer-start-login-required"));
    }
    const parsed = errorResponseSchema.safeParse(payload);
    throw new ApiError(
      parsed.success ? parsed.data.error : "De server gaf een onverwacht antwoord.",
      response.status,
      payload,
    );
  }
  const parsed = schema.safeParse(payload);
  if (!parsed.success) throw new ApiError("De servergegevens hebben een onverwacht formaat.", 502);
  return parsed.data;
}

export function fetchPersonas() {
  return request("/api/session/personas", demoPersonaSchema.array());
}

export function fetchSession() {
  return request("/api/session", currentSessionSchema);
}

export function selectSession(personaId: string) {
  const body = demoSessionSelectionSchema.parse({ personaId });
  return request("/api/session/select", sessionMutationResultSchema, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function clearSession() {
  return request("/api/session/clear", sessionMutationResultSchema, {
    method: "POST",
    body: "{}",
  });
}

export function fetchBootstrap() {
  return request("/api/bootstrap", prototypeBootstrapSchema);
}

export function fetchAdministration() {
  return request("/api/admin/overview", platformAdministrationSchema);
}

export function sendCommand(command: PrototypeCommand) {
  return request("/api/commands", commandResultSchema, {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify(command),
  });
}
